import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
    CITY_REQUIRED,
    CITY_TOO_SHORT,
    COMPANY_REQUIRED,
    COMPANY_TOO_SHORT,
    DATE_INVALID_FORMAT,
    MAIL_REQUIRED,
    NAME_TOO_SHORT,
    PHONE_INVALID,
    PHONE_REQUIRED,
    STATE_REQUIRED,
    STREET_REQUIRED,
    STREET_TOO_SHORT,
    ZIP_REQUIRED,
} from '@config/error-messages.const';
import { states } from '@config/states';
import { filter, map, startWith, take, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { InsurenetInterfaceService, TitlesService } from '@core';
import { BillingAddress } from '@models/billing-address';
import { Insurant } from '@models/insurant';
import { CommonValidators } from '../../../shared/validators/common-validators';
import { OrderStepBaseComponent } from '../../order-step-base-component';
import { OrderState } from 'app/order/models/order-state.model';
import { City } from '@models/city';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-billing-address-form',
    templateUrl: './billing-address-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BillingAddressFormComponent extends OrderStepBaseComponent<Pick<OrderState, 'billingAddress'>> {
    private titlesService = inject(TitlesService);
    private formBuilder = inject(FormBuilder);
    private insurenetInterface = inject(InsurenetInterfaceService);

    public billingAddress = input.required<Partial<BillingAddress>>();
    public insurants = input.required<Insurant[]>();

    public form = this.formBuilder.group({
        title: [null as number | null, Validators.required],
        firstName: ['', CommonValidators.shortSomething],
        lastName: ['', CommonValidators.shortSomething],
        company: [''],
        street: ['', CommonValidators.something],
        zip: ['', CommonValidators.zip],
        city: ['', CommonValidators.shortSomething],
        state: [null as string | null, Validators.required],
        phone: ['', CommonValidators.phone],
        mail: ['', [Validators.required, Validators.email]],
        dateOfBirth: ['', CommonValidators.birthday(18, 99)],
    });

    private titleValue = toSignal(
        this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)),
    );
    private maxAge = computed(() =>
        this.product().orderConfig.insurantMaxAge > 18 ? this.product().orderConfig.insurantMaxAge : 99,
    );

    protected titles = toSignal(this.titlesService.titles$);
    protected isCompany = computed(() => this.titleValue() === this.companyTitleId);
    protected companyTitleId = environment.titleCompanyId;
    protected showMinorsNote = signal<boolean>(true);
    protected states = [...states];

    // #region errormessages
    protected firstNameErrorMessages: string = NAME_TOO_SHORT;
    protected lastNameErrorMessages: string = NAME_TOO_SHORT;
    protected dateOfBirthErrorMessages = computed(() => ({
        required: DATE_INVALID_FORMAT,
        ageTooHigh: `Die versicherte Person darf nicht Älter als ${this.maxAge()} Jahre sein.
        Wenn du diese Versicherung für eine andere Person abschließt, deren Erziehungsberechtigter oder Vormund du bist gehe bitte einen Schritt zurück und gib die versicherte Person an.`,
        ageTooLow: `Du musst mindestens 18 Jahre alt sein, um eine Versicherung abschließen zu können.
            Wenn du diese Versicherung für eine andere, minderjährige Person abschließt, deren Erziehungsberechtigter oder Vormund du bist, gehe bitte einen Schritt zurück und gib die versicherte Person an.`,
        invalidDateFormat: DATE_INVALID_FORMAT,
    }));
    protected streetErrorMessages = { required: STREET_REQUIRED, minlength: STREET_TOO_SHORT };
    protected zipErrorMessages: string = ZIP_REQUIRED;
    protected cityErrorMessages = {
        required: CITY_REQUIRED,
        minlength: CITY_TOO_SHORT,
    };
    protected phoneErrorMessages = { pattern: PHONE_INVALID, required: PHONE_REQUIRED };
    protected mailErrorMessages: string = MAIL_REQUIRED;
    protected companyErrorMessages = {
        required: COMPANY_REQUIRED,
        minlength: COMPANY_TOO_SHORT,
    };
    protected stateErrorMessages: string = STATE_REQUIRED;
    // #endregion

    public constructor() {
        super();

        // update form from insuree when it changes, but do not overwrite
        // values with empty properties from the insuree
        effect(() => {
            const insuree = this.insurants().find((insurant) => insurant.isInsuree);
            if (insuree) {
                for (const key in insuree) {
                    if (!Object.hasOwn(insuree, key)) {
                        continue;
                    }
                    if (key === 'title') {
                        if (typeof insuree.title?.id === 'number') {
                            this.form.get('title').patchValue(insuree.title.id);
                        }
                        continue;
                    }
                    if (insuree[key]) {
                        this.form.get(key)?.patchValue(insuree[key]);
                    }
                }
                if (this.product()?.orderConfig.disableBirthdayInBillingAddress) {
                    this.form.get('dateOfBirth').disable();
                    this.showMinorsNote.set(false);
                }
            } else {
                this.form.get('dateOfBirth').enable();
                this.showMinorsNote.set(true);
            }
            this.form.updateValueAndValidity();
        });

        // update form when billing address changes
        effect(() => {
            const billingAddress = this.billingAddress();
            if (billingAddress) {
                this.form.patchValue({ ...billingAddress, title: billingAddress.title?.id ?? null });
            }
        });

        // update form when user is company
        effect(() => {
            if (this.isCompany()) {
                this.form.controls.dateOfBirth.setValidators([]);
                this.form.controls.dateOfBirth.patchValue('');
                this.form.controls.company.setValidators(CommonValidators.something);
            } else {
                this.form.controls.dateOfBirth.setValidators(CommonValidators.birthday(18, this.maxAge()));
                this.form.controls.company.setValidators([]);
                this.form.controls.company.patchValue('');
            }
            this.form.updateValueAndValidity();
        });

        // update birthdate validator when max age changes
        effect(() => {
            this.maxAge();
            if (!this.isCompany()) {
                untracked(() => this.updateDateOfBirthValidators());
            }
        });

        // set title to company if only companies are allowed
        effect(() => {
            if (this.product().orderConfig.companiesOnly) {
                untracked(() => this.patchCompanyTitle());
            }
        });

        // fetch city for valid zip value
        const zipValue = toSignal(
            this.form.controls.zip.valueChanges.pipe(
                startWith(this.form.controls.zip.value),
                filter(() => this.form.get('zip').valid),
            ),
        );
        effect(() => {
            const zip = zipValue();
            if (zip) {
                untracked(() =>
                    this.insurenetInterface
                        .getCity(zip)
                        .pipe(map((city: City) => city.name))
                        .subscribe((cityName: string) => this.form.controls.city.patchValue(cityName)),
                );
            }
        });
    }

    private patchCompanyTitle(): void {
        this.titlesService.companyTitle$
            .pipe(
                filter((title) => title !== null),
                take(1),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((title) => this.form.get('title').patchValue(title.id));
    }

    private updateDateOfBirthValidators(): void {
        this.form.get('dateOfBirth').setValidators(CommonValidators.birthday(18, this.maxAge()));
        this.form.get('dateOfBirth').updateValueAndValidity();
    }

    public getData(): Partial<OrderState> {
        const insurants = [...this.insurants()];
        const i = insurants.findIndex((insurant) => insurant.isInsuree);
        if (i !== -1) {
            insurants[i] = {
                ...insurants[i],
                firstName: this.form.get('firstName').value,
                lastName: this.form.get('lastName').value,
                dateOfBirth: this.form.get('dateOfBirth').value,
                title: this.titlesService.titleFromId(this.form.get('title').value),
            };
        }
        return {
            billingAddress: {
                ...this.form.value,
                dateOfBirth: this.form.get('dateOfBirth').value,
                title: this.titlesService.titleFromId(this.form.get('title').value),
            },
            insurants,
        };
    }
}
